"use client";

import { Label } from "@/components/ui/label";
import { GetVideoResponseType } from "../../hooks/useGetVideo";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useEffect, useState } from "react";
import {
    Copy,
    Check,
    Globe,
    Lock,
    LucideIcon,
    Share2,
    Code2,
    Play,
    BookOpen,
} from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getDirectPlayUrl, getVideoEmbedUrlWithOptions, cn } from "@/lib/utils";
import toast from "@/lib/toast";
import { client } from "@/lib/hono";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useQueryClient } from "@tanstack/react-query";
import {
    PlayerMode,
    PlayerPresets,
    PlayerPresetSettings,
    VideoPrivacyType,
} from "@yedalabs/schema";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

type EmbedOptionId = keyof PlayerPresetSettings;

const defaultPlayerPresetSettings: PlayerPresetSettings = {
    autoPlay: false,
    muted: false,
    captions: false,
    disableSeeking: false,
    englishPlayer: false,
};

const embedOptionDefs: { id: EmbedOptionId; keyName: string }[] = [
    { id: "autoPlay", keyName: "video-sheet.embed-options.autoPlay.title" },
    { id: "muted", keyName: "video-sheet.embed-options.muted.title" },
    { id: "captions", keyName: "video-sheet.embed-options.captions.title" },
    {
        id: "disableSeeking",
        keyName: "video-sheet.embed-options.disableSeeking.title",
    },
    {
        id: "englishPlayer",
        keyName: "video-sheet.embed-options.english-player.title",
    },
];

const privacyOptions: {
    value: VideoPrivacyType;
    keyName: string;
    icon: LucideIcon;
}[] = [
    {
        value: "public",
        keyName: "storage.video-privacy-settings.public",
        icon: Globe,
    },
    {
        value: "private",
        keyName: "storage.video-privacy-settings.private",
        icon: Lock,
    },
];

const isOptionEnabled = (s: PlayerPresetSettings, id: EmbedOptionId) => s[id];

const toggleOption = (
    s: PlayerPresetSettings,
    id: EmbedOptionId
): PlayerPresetSettings => ({ ...s, [id]: !s[id] });

function PlayerModePicker({
    value,
    onChange,
    hasChapters,
    dir,
}: {
    value: PlayerMode;
    onChange: (mode: PlayerMode) => void;
    hasChapters: boolean;
    dir: "ltr" | "rtl";
}) {
    const options: {
        id: PlayerMode;
        labelKey: string;
        icon: LucideIcon;
        disabled: boolean;
    }[] = [
        {
            id: "video",
            labelKey: "video-sheet.aside.card.player-mode.video",
            icon: Play,
            disabled: false,
        },
        {
            id: "lomda",
            labelKey: "video-sheet.aside.card.player-mode.lomda",
            icon: BookOpen,
            disabled: !hasChapters,
        },
    ];

    return (
        <TooltipProvider>
            <Tabs
                value={value}
                onValueChange={(v) => onChange(v as PlayerMode)}
                dir={dir}
            >
                <TabsList className="bg-white border w-full flex justify-start gap-1">
                    {options.map((opt) => {
                        const Icon = opt.icon;

                        if (opt.disabled) {
                            return (
                                <Tooltip key={opt.id}>
                                    <TooltipTrigger asChild>
                                        <div className="flex-1">
                                            <TabsTrigger
                                                value={opt.id}
                                                disabled
                                                className="data-[state=active]:bg-secondary gap-1 w-full"
                                            >
                                                <Icon className="size-4" />
                                                {t(opt.labelKey)}
                                            </TabsTrigger>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {t(
                                            "video-sheet.aside.card.player-mode.lomda.disabled"
                                        )}
                                    </TooltipContent>
                                </Tooltip>
                            );
                        }

                        return (
                            <TabsTrigger
                                key={opt.id}
                                value={opt.id}
                                className="data-[state=active]:bg-secondary gap-1 flex-1"
                            >
                                <Icon className="size-4" />
                                {t(opt.labelKey)}
                            </TabsTrigger>
                        );
                    })}
                </TabsList>
            </Tabs>
        </TooltipProvider>
    );
}

function PlaybackOptions({
    settings,
    onToggle,
}: {
    settings: PlayerPresetSettings;
    onToggle: (id: EmbedOptionId) => void;
}) {
    return (
        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
            {embedOptionDefs.map((o) => {
                const checked = isOptionEnabled(settings, o.id);
                return (
                    <label
                        key={o.id}
                        className="flex flex-row items-center gap-1.5 cursor-pointer"
                    >
                        <Checkbox
                            checked={checked}
                            onCheckedChange={() => onToggle(o.id)}
                            className="size-3.5"
                        />
                        <span className="text-xs font-normal text-foreground select-none">
                            {t(o.keyName)}
                        </span>
                    </label>
                );
            })}
        </div>
    );
}

function CopyableLink({
    value,
    ariaLabel,
    disabledReason,
}: {
    value: string;
    ariaLabel?: string;
    disabledReason?: string;
}) {
    const [copied, setCopied] = useState(false);
    const isDisabled = !!disabledReason;

    const handleCopy = () => {
        if (copied || isDisabled) return;
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const inner = (
        <div className={cn("relative", isDisabled && "cursor-not-allowed")}>
            <Input
                disabled
                aria-label={ariaLabel}
                className={cn(
                    "!cursor-default !opacity-100 text-muted-foreground pe-8 font-mono text-[11px] leading-none h-8 py-0 bg-muted/40 truncate",
                    isDisabled && "!cursor-not-allowed pointer-events-none"
                )}
                value={value}
            />
            <button
                type="button"
                onClick={handleCopy}
                aria-disabled={isDisabled}
                aria-label={
                    isDisabled
                        ? disabledReason
                        : copied
                        ? t("video-sheet.aside.copied")
                        : t("video-sheet.aside.copy")
                }
                className={cn(
                    "absolute inset-y-0 end-0.5 my-0.5 flex items-center justify-center px-1.5 rounded transition-colors",
                    isDisabled
                        ? "text-muted-foreground/70 cursor-not-allowed"
                        : copied
                        ? "text-green-600"
                        : "text-muted-foreground hover:text-foreground hover:bg-background"
                )}
            >
                {isDisabled ? (
                    <Lock className="size-3.5" />
                ) : copied ? (
                    <Check className="size-3.5" />
                ) : (
                    <Copy className="size-3.5" />
                )}
            </button>
        </div>
    );

    if (!isDisabled) return inner;

    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>{inner}</TooltipTrigger>
                <TooltipContent
                    side="bottom"
                    className="max-w-[240px] text-center bg-black text-white fill-black"
                >
                    {disabledReason}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function SectionCard({
    icon: Icon,
    title,
    description,
    children,
}: {
    icon: LucideIcon;
    title: string;
    description: string;
    children: React.ReactNode;
}) {
    return (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="px-4 pt-2.5 pb-2.5 border-b">
                <div className="flex items-start gap-2.5">
                    <div className="flex items-center justify-center size-8 rounded-lg bg-secondary text-secondary-foreground shrink-0">
                        <Icon className="size-4" />
                    </div>
                    <div className="flex flex-col">
                        <h3 className="text-sm font-semibold text-foreground leading-tight">
                            {title}
                        </h3>
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                            {description}
                        </p>
                    </div>
                </div>
            </div>
            <div className="p-3.5 pt-1.5 flex flex-col gap-3">{children}</div>
        </div>
    );
}

export default function VideoSheetAside({
    video,
}: {
    video: GetVideoResponseType["data"];
}) {
    const { dir } = useLocale();
    const hasChapters = (video.chapters?.length ?? 0) > 0;
    const [loadingPrivacy, setLoadingPrivacy] = useState(false);

    const initialPresets: PlayerPresets = video.playerPresets ?? {
        mode: "video",
        share: defaultPlayerPresetSettings,
        embed: defaultPlayerPresetSettings,
    };

    const [mode, setMode] = useState<PlayerMode>(initialPresets.mode);
    const [shareSettings, setShareSettings] = useState<PlayerPresetSettings>(
        initialPresets.share
    );
    const [embedSettings, setEmbedSettings] = useState<PlayerPresetSettings>(
        initialPresets.embed
    );

    useEffect(() => {
        if (!hasChapters && mode === "lomda") setMode("video");
    }, [hasChapters, mode]);

    const queryClient = useQueryClient();

    const directPlayUrl = getDirectPlayUrl(video.pullZone!, video.id, {
        ...shareSettings,
        mode,
    });

    const embedUrl = getVideoEmbedUrlWithOptions(video.pullZone!, video.id, {
        ...embedSettings,
        mode,
    });

    const itemNoun = t(
        mode === "lomda"
            ? "video-sheet.aside.item.lomda"
            : "video-sheet.aside.item.video"
    );

    const changeVideoPrivacy = async (privacy: VideoPrivacyType) => {
        setLoadingPrivacy(true);
        await toast.promise(
            client.api.storage.video[":videoId"].update
                .$post({
                    param: { videoId: video.id },
                    json: { privacy },
                })
                .then((res) => {
                    if (res.ok) {
                        queryClient.setQueryData(
                            ["get-video", video.id],
                            (oldData: GetVideoResponseType) => {
                                if (!oldData) return oldData;
                                return {
                                    ...oldData,
                                    data: {
                                        ...oldData.data,
                                        privacy,
                                    },
                                };
                            }
                        );
                    } else throw new Error("Error");
                }),
            {
                loading: t("toast.loading.change-video-privacy"),
                success: t("toast.success.change-video-privacy"),
                error: t("toast.error.change-video-privacy"),
            },
            {
                loading: { keyName: "toast.loading.change-video-privacy" },
                success: { keyName: "toast.success.change-video-privacy" },
                error: { keyName: "toast.error.change-video-privacy" },
            }
        );
        setLoadingPrivacy(false);
    };

    return (
        <aside className="flex flex-col w-full lg:w-[300px] xl:w-[340px] py-4 px-4 lg:pe-0 gap-4">
            <PlayerModePicker
                value={mode}
                onChange={setMode}
                hasChapters={hasChapters}
                dir={dir}
            />

            <SectionCard
                icon={Share2}
                title={t("video-sheet.aside.share-card.title")}
                description={t(
                    "video-sheet.aside.share-card.description",
                    undefined,
                    { item: itemNoun }
                )}
            >
                <div>
                    <Label className="text-xs font-medium text-muted-foreground">
                        {t("video-sheet.aside.card.select-video-privacy.title")}
                    </Label>
                    <Select
                        disabled={loadingPrivacy}
                        value={video.privacy}
                        onValueChange={(value) =>
                            changeVideoPrivacy(value as VideoPrivacyType)
                        }
                        dir={dir}
                    >
                        <SelectTrigger className="mt-1 h-8 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {privacyOptions.map((option) => (
                                <SelectItem
                                    key={option.value}
                                    value={option.value}
                                    className="text-xs"
                                >
                                    <div className="flex flex-row items-center gap-2">
                                        <option.icon className="size-3.5" />
                                        {t(option.keyName)}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div>
                    <Label className="text-xs font-medium text-muted-foreground">
                        {t("video-sheet.aside.card.direct-play-link.title")}
                    </Label>
                    <div className="mt-1">
                        <CopyableLink
                            value={directPlayUrl}
                            ariaLabel={t(
                                "video-sheet.aside.card.direct-play-link.title"
                            )}
                            disabledReason={
                                video.privacy !== "public"
                                    ? t(
                                          "video-sheet.aside.card.direct-play-link.warning"
                                      )
                                    : undefined
                            }
                        />
                    </div>
                    <div className="mt-2">
                        <PlaybackOptions
                            settings={shareSettings}
                            onToggle={(id) =>
                                setShareSettings((s) => toggleOption(s, id))
                            }
                        />
                    </div>
                </div>
            </SectionCard>

            <SectionCard
                icon={Code2}
                title={t("video-sheet.aside.embed-card.title")}
                description={t(
                    "video-sheet.aside.embed-card.description",
                    undefined,
                    { item: itemNoun }
                )}
            >
                <div>
                    <Label className="text-xs font-medium text-muted-foreground">
                        {t("video-sheet.aside.card.embed.title")}
                    </Label>
                    <div className="mt-1">
                        <CopyableLink
                            value={embedUrl}
                            ariaLabel={t("video-sheet.aside.card.embed.title")}
                        />
                    </div>
                    <div className="mt-2">
                        <PlaybackOptions
                            settings={embedSettings}
                            onToggle={(id) =>
                                setEmbedSettings((s) => toggleOption(s, id))
                            }
                        />
                    </div>
                </div>
            </SectionCard>
        </aside>
    );
}
